import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { measureProductAction, trackProductPage } from '../../lib/productAnalytics';

const AdminLogin: React.FC = () => {
  const { user, loading, error, signIn } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const tracked = React.useRef(false);
  React.useEffect(() => {
    if (loading || user || tracked.current) return;
    tracked.current = true;
    trackProductPage('/admin/login');
  }, [loading, user]);

  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/admin';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      await measureProductAction('sign_in', () => signIn(email.trim(), password));
    } catch {
      // AuthContext translates and displays the Firebase error in the form.
    } finally {
      setSubmitting(false);
    }
  };

  if (!loading && user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <main className="admin-shell min-h-screen px-4 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center">
        <Card className="w-full bg-[var(--admin-surface-strong)]">
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--admin-cyan-soft)] text-[var(--admin-cyan)]">
              <LockKeyhole size={22} />
            </div>
            <CardTitle className="text-2xl">Admin sign in</CardTitle>
            <p className="text-sm text-[var(--admin-muted)]">
              Use a Firebase admin account with the required admin claim.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email"
                autoComplete="email"
                className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-4 py-3 text-sm"
                required
              />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                className="w-full rounded-2xl border border-[var(--admin-border)] bg-white px-4 py-3 text-sm"
                required
              />
              {error ? (
                <div role="alert" className="rounded-2xl border border-[var(--admin-rose)] bg-[var(--admin-rose-soft)] px-4 py-3 text-sm text-[var(--admin-rose)]">
                  {error}
                </div>
              ) : null}
              <button
                type="submit"
                disabled={loading || submitting}
                className="w-full rounded-2xl bg-[var(--admin-text)] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default AdminLogin;
